import Promise from 'bluebird';
import _ from 'underscore';
import { useEffect } from 'react';
import type { Currency } from '@bingads-webui-clientcenter/entity-utils';

import { useAction } from './hooks/use-data-actions';
import { useCache } from './context/cache-context';
import { getCCJsDataObj } from './ccui-util';
import type {
  CCDataServerContext,
  CCJsDataRepositories,
  CountryTimeZonesMapping,
  CurrencyCountriesMapping,
  DomainCountry,
  DomainTimeZone,
  EntityId,
} from './types';

export class CCUIDomainActions {
  aid: EntityId;
  ccuiEndpoint: string;
  cid: EntityId;
  jsDataCcRest: CCJsDataRepositories;
  uid: EntityId;

  constructor({
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
    ServicesEndpoints
  }: CCDataServerContext) {
    this.aid = CurrentAccount && CurrentAccount.Id;
    this.cid = CurrentCustomer && CurrentCustomer.Id;
    this.uid = CurrentUser && CurrentUser.Id;
    this.ccuiEndpoint = ServicesEndpoints.ClientCenterUIAPI;

    this.jsDataCcRest = getCCJsDataObj(this.cid, this.uid);
  }

  countryFetch = () => Promise.resolve(this.jsDataCcRest.DomainDataCountry.findAll());

  stateOrProvinceFetch = (countryCode: string) =>
    Promise.resolve(this.jsDataCcRest.DomainDataSubGeography.findAll({ countryCode }));

  timeZoneFetch = () => this.jsDataCcRest.DomainDataTimeZone.findAll();

  currencyFetch = () => this.jsDataCcRest.DomainDataCurrency.findAll();

  currencyCountriesMappingFetch = () =>
    Promise.resolve(this.jsDataCcRest.CurrencyCountriesMapping.findAll());

  countryTimeZonesMappingFetch = () =>
    Promise.resolve(this.jsDataCcRest.CountryTimeZonesMapping.findAll());
}

export const useDataDomainCountries = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } = useCache<DomainCountry[]>('domainDataCountries');
  const domainActions = useAction(CCUIDomainActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && domainActions) {
      tryUpdate(() => domainActions.countryFetch());
    }
  }, [domainActions, cache, tryUpdate]);

  return {
    domainDataCountries: cache,
  };
};

export const useDataDomainTimeZones = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } = useCache<DomainTimeZone[]>('domainDataTimeZones');
  const domainActions = useAction(CCUIDomainActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && domainActions) {
      tryUpdate(() => domainActions.timeZoneFetch());
    }
  }, [domainActions, cache, tryUpdate]);

  return {
    domainDataTimeZones: cache,
  };
};

export const useDataDomainCurrencies = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } = useCache<Currency[]>('domainDataCurrencies');
  const domainActions = useAction(CCUIDomainActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && domainActions) {
      tryUpdate(() => domainActions.currencyFetch()
        .then(domainDataCurrencies => _.pluck(domainDataCurrencies, 'value')));
    }
  }, [domainActions, cache, tryUpdate]);

  return {
    domainDataCurrencies: cache,
  };
};

export const useDataDomainCurrencyCountriesMapping = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } =
    useCache<CurrencyCountriesMapping[]>('domainDataCurrencyCountriesMapping');
  const domainActions = useAction(CCUIDomainActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && domainActions) {
      tryUpdate(() => domainActions.currencyCountriesMappingFetch());
    }
  }, [domainActions, cache, tryUpdate]);

  return {
    domainDataCurrencyCountriesMapping: cache,
  };
};

export const useDataDomainCountryTimeZonesMapping = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } =
    useCache<CountryTimeZonesMapping[]>('domainDataCountryTimeZonesMapping');
  const domainActions = useAction(CCUIDomainActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && domainActions) {
      tryUpdate(() => domainActions.countryTimeZonesMappingFetch());
    }
  }, [domainActions, cache, tryUpdate]);

  return {
    domainDataCountryTimeZonesMapping: cache,
  };
};

export const useCCUIDomainDataAction = (serverContext: CCDataServerContext) =>
  useAction(CCUIDomainActions, serverContext);
